import React, { useState } from 'react';
import './ContactSectionHome.css';
import { useForm, SubmitHandler } from 'react-hook-form';
import ShinyButton from '../../Common/ShinyButton/ShinyButton';
import { useRecaptcha } from '../../../hooks/useRecaptcha';
import { useTranslation } from 'react-i18next';
import { getApiUrl, API_ENDPOINTS } from '../../../utils/api';

interface ContactFormData {
  company: string;
  name: string;
  email: string;
  phone: string;
  communication: string;
  message: string;
}

const ContactSection: React.FC = () => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isSubmitSuccessful },
  } = useForm<ContactFormData>();

  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const { t } = useTranslation();

  const { isLoaded, isReady } = useRecaptcha();

  const onSubmit: SubmitHandler<ContactFormData> = async (data) => {
    setSubmissionError(null);
    
    if (!isReady || !window.grecaptcha) {
      setSubmissionError(t('contact.form.recaptcha.notReady', 'reCAPTCHA n\'est pas encore prêt. Veuillez patienter quelques secondes et réessayer.'));
      return;
    }

    try {
      console.log('🤖 Génération token reCAPTCHA...');
      const token = await window.grecaptcha.execute(process.env.REACT_APP_RECAPTCHA_SITE_KEY!, {
        action: 'contact_submit'
      }).catch((error: any) => {
        console.error('❌ Erreur génération token reCAPTCHA:', error);
        throw new Error('Erreur lors de la génération du token reCAPTCHA');
      });
      console.log('✅ Token reCAPTCHA généré');
      
      const apiUrl = getApiUrl(API_ENDPOINTS.CONTACT);
      
      console.log('🚀 Envoi requête au serveur:', apiUrl);
      console.log('🔧 Environment:', process.env.NODE_ENV);
      console.log('🔧 API Endpoint:', API_ENDPOINTS.CONTACT);
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...data, recaptchaToken: token }),
      });

      console.log('📥 Réponse du serveur:', {
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
        url: response.url
      });

      if (response.ok) {
        const successData = await response.json();
        console.log('Contact form submission successful:', successData);
        reset();
      } else {
        const responseText = await response.text();
        console.error('📥 Réponse erreur (texte brut):', responseText);
        
        try {
          const errorData = JSON.parse(responseText);
          console.error('Contact form submission failed:', errorData.message);
          setSubmissionError(errorData.message || t('contact.form.error', "Une erreur est survenue lors de l'envoi du message. Veuillez réessayer plus tard."));
        } catch (parseError) {
          console.error('❌ Impossible de parser la réponse JSON:', parseError);
          setSubmissionError(t('contact.form.error', "Une erreur est survenue lors de l'envoi du message. Veuillez réessayer plus tard."));
        }
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      setSubmissionError(
        t('contact.form.error', "Une erreur est survenue lors de l'envoi du message. Veuillez réessayer plus tard.")
      );
    }
  };

  return (
    <section className="section-form">
      <div className="contact-section">
        <div className="container-form">
          <div className="grid-container-form">
            <div className="form-section-form">
              <h2 className="blue-h2-contact">{t('contact.title', 'Contact')}</h2>
              {isSubmitSuccessful && (
                <p className="text-green-500 mb-4">
                  {t('contact.form.success', 'Message envoyé avec succès!')}
                </p>
              )}
              {submissionError && (
                <p className="text-red-500 mb-4">{submissionError}</p>
              )}
              {!isReady && (
                <p className="text-blue-500 mb-4">
                  {t('contact.form.recaptcha.loading', 'Chargement de la sécurité en cours...')}
                </p>
              )}
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div>
                  <label htmlFor="company" className="visually-hidden"></label>
                  <input
                    id="company"
                    type="text"
                    {...register('company', {
                      required: false,
                    })}
                    className={`form-input-form ${errors.company ? 'error' : ''}`}
                    placeholder={t('contact.form.company', 'Société')}
                  />
                  {errors.company && <span className="error-message">{t('contact.form.errors.company', 'Erreur dans le champ Société.')}</span>}
                </div>

                <div>
                  <label htmlFor="name" className="visually-hidden"></label>
                  <input
                    id="name"
                    type="text"
                    {...register('name', { required: true })}
                    className={`form-input-form ${errors.name ? 'error' : ''}`}
                    placeholder={t('contact.form.name', 'Nom')}
                  />
                  {errors.name && <span className="error-message">{t('contact.form.errors.name', 'Le nom est requis.')}</span>}
                </div>

                <div>
                  <label htmlFor="email" className="visually-hidden"></label>
                  <input
                    id="email"
                    type="email"
                    {...register('email', {
                      required: true,
                      pattern: {
                        value: /^\S+@\S+$/i,
                        message: t('contact.form.errors.emailInvalid', "Veuillez entrer un email valide."),
                      },
                    })}
                    className={`form-input-form ${errors.email ? 'error' : ''}`}
                    placeholder={t('contact.form.email', 'E-mail')}
                  />
                  {errors.email && <span className="error-message">{errors.email.message || t('contact.form.errors.emailRequired', 'L\'email est requis.')}</span>}
                </div>

                <div>
                  <label htmlFor="phone" className="visually-hidden"></label>
                  <input
                    id="phone"
                    type="tel"
                    {...register('phone', { required: true })}
                    className={`form-input-form ${errors.phone ? 'error' : ''}`}
                    placeholder={t('contact.form.phone', 'Téléphone')}
                  />
                  {errors.phone && <span className="error-message">{t('contact.form.errors.phone', 'Le téléphone est requis.')}</span>}
                </div>

                <div>
                  <label htmlFor="message" className="visually-hidden"></label>
                  <textarea
                    id="message"
                    {...register('message', { required: true })}
                    className={`form-input-form ${errors.message ? 'error' : ''} message-field`}
                    placeholder={t('contact.form.message', 'Message')}
                    rows={2}
                  ></textarea>
                  {errors.message && <span className="error-message">{t('contact.form.errors.message', 'Le message est requis.')}</span>}
                </div>

                <div className="preferred-method-form">
                  <h4>{t('contact.form.preferredMethod', 'Je souhaite être contacté par')}</h4>
                  <div className="radio-group-form">
                    <label className="radio-option-form">
                      <input
                        type="radio"
                        {...register('communication', {
                          required: true,
                        })}
                        value="Email"
                        className={`${errors.communication ? 'error-radio' : ''}`}
                      />
                      <span className="radio-custom-form"></span> {t('contact.form.preferredMethods.email', 'E-mail')}
                    </label>
                    <label className="radio-option-form">
                      <input
                        type="radio"
                        {...register('communication', {
                          required: true,
                        })}
                        value="Phone"
                        className={`${errors.communication ? 'error-radio' : ''}`}
                      />
                      <span className="radio-custom-form"></span> {t('contact.form.preferredMethods.phone', 'Téléphone')}
                    </label>
                  </div>
                  {errors.communication && <span className="error-message">{t('contact.form.errors.preferredMethod', 'Veuillez choisir une méthode de contact.')}</span>}
                </div>

                <ShinyButton 
                  type="submit" 
                  disabled={isSubmitting || !isReady}
                >
                  {isSubmitting 
                    ? t('contact.form.sending', 'Envoi en cours...') 
                    : !isReady 
                      ? t('contact.form.recaptcha.loading', 'Chargement...') 
                      : t('contact.form.send', 'Envoyer')
                  }
                </ShinyButton>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;